import { Transition } from '@headlessui/react';
import { RiCloseLine, RiUserLine, RiCheckDoubleLine } from '@remixicon/react';
import { Fragment, useState } from 'react';
import { Badge, Button, Card, Text, Title } from '@tremor/react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useQueryClient } from 'react-query';
import notificationsApi from '../../../../network/notifications';
import { getNotificationLink } from '../../../../utils/notifications';

const API_HOST = process.env.REACT_APP_API_HOST;

export default function Notifications({
  isOpen,
  setOpen,
  notifications,
  onClearNotification,
}: {
  isOpen: boolean;
  setOpen: (isOpen: boolean) => void;
  notifications: any[];
  onClearNotification: (id: string | number) => void;
}) {
  const queryClient = useQueryClient();
  const [isMarkingAll, setIsMarkingAll] = useState(false);

  const handleMarkAllAsRead = async () => {
    setIsMarkingAll(true);
    try {
      await axios.patch(`${API_HOST}/v1/notification/read-all`);
      queryClient.invalidateQueries('notifications');
      queryClient.invalidateQueries('unread-count');
      queryClient.invalidateQueries('notifications-paginated');
    } catch (error) {
      console.warn('Failed to mark all notifications as read', error);
    } finally {
      setIsMarkingAll(false);
    }
  };

  // Opening a notification marks it read: straight away in the cached list,
  // then on the server.
  const handleOpenNotification = async (item: any) => {
    if (item.read) return;

    queryClient.setQueryData(['notifications'], (current: any) =>
      Array.isArray(current?.notifications)
        ? {
            ...current,
            notifications: current.notifications.map((notification: any) =>
              notification.id === item.id
                ? { ...notification, read: true }
                : notification,
            ),
          }
        : current,
    );

    try {
      await notificationsApi.markAsRead(String(item.id));
      queryClient.invalidateQueries('notifications');
      queryClient.invalidateQueries('unread-count');
      queryClient.invalidateQueries('notifications-paginated');
    } catch (error) {
      console.warn('Failed to mark notification as read', error);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <div className="relative">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 -translate-x-4"
          enterTo="opacity-100 translate-x-0"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 translate-x-0"
          leaveTo="opacity-0 -translate-x-4"
        >
          <div className="fixed left-[260px] top-0 z-50 flex h-screen min-w-[400px] max-w-[450px] flex-col rounded-xl border bg-white shadow-lg dark:border-dark-tremor-border dark:bg-dark-tremor-background">
            <div className="flex items-center justify-between border-b border-tremor-border px-6 py-4 dark:border-dark-tremor-border">
              <div className="flex flex-row items-center gap-3">
                <Title className="text-lg">Notifications</Title>
                <Badge color="rose">{notifications?.length || 0}</Badge>
              </div>
              <div className="flex items-center gap-2">
                {notifications?.length > 0 && (
                  <Button
                    icon={RiCheckDoubleLine}
                    onClick={handleMarkAllAsRead}
                    variant="light"
                    color="gray"
                    size="xs"
                    tooltip="Mark all as read"
                    disabled={isMarkingAll}
                    className="text-tremor-content hover:bg-tremor-background-muted dark:text-dark-tremor-content dark:hover:bg-dark-tremor-background-muted"
                  >
                    Mark all read
                  </Button>
                )}
                <Button
                  icon={RiCloseLine}
                  onClick={() => setOpen(false)}
                  variant="light"
                  color="gray"
                  tooltip="Close notifications panel"
                  className="text-tremor-content hover:bg-tremor-background-muted dark:text-dark-tremor-content dark:hover:bg-dark-tremor-background-muted"
                />
              </div>
            </div>
            <div className="flex-1 space-y-4 overflow-y-auto p-4">
              {notifications?.length > 0 ? (
                notifications?.map((item: any) => (
                  <Card
                    key={item.id}
                    className={`relative p-0 hover:bg-white dark:hover:bg-dark-tremor-background ${
                      item.read ? 'opacity-50' : ''
                    }`}
                  >
                    {(() => {
                      const link = getNotificationLink(item);
                      const className =
                        'block rounded-tremor-default p-4 hover:bg-tremor-background-muted dark:hover:bg-dark-tremor-background-muted';
                      const content = (
                        <div className="flex flex-row items-start gap-3">
                          <div className="relative mt-1 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-tremor-background-subtle dark:bg-dark-tremor-background-subtle">
                            <RiUserLine
                              className="h-5 w-5 text-tremor-content-subtle dark:text-dark-tremor-content-subtle"
                              aria-hidden="true"
                            />
                            {!item.read && (
                              <div className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-red-500" />
                            )}
                          </div>
                          <div className="flex flex-1 flex-col gap-1">
                            <Text>
                              <span className="font-medium text-tremor-content-strong dark:text-dark-tremor-content-strong">
                                {item?.user?.full_name}
                              </span>{' '}
                              {item.message}
                            </Text>
                            {item.content && (
                              <Text className="text-tremor-content-subtle dark:text-dark-tremor-content-subtle">
                                {item.content}
                              </Text>
                            )}
                            {item.comment && (
                              <div className="mt-1 rounded-tremor-small border border-tremor-border bg-tremor-background-muted p-2 text-tremor-default dark:border-dark-tremor-border dark:bg-dark-tremor-background-muted">
                                <Text className="font-medium text-tremor-content-strong dark:text-dark-tremor-content-strong">
                                  Comment
                                </Text>
                                <Text>{item.comment}</Text>
                              </div>
                            )}
                            <Text className="mt-1 text-sm text-tremor-content-subtle dark:text-dark-tremor-content-subtle">
                              {item.time}
                            </Text>
                          </div>
                        </div>
                      );
                      // Notifications with nowhere to go (such as a request
                      // notification without a request_id) are not links;
                      // opening either kind marks it read.
                      return link ? (
                        <Link
                          to={link}
                          onClick={() => handleOpenNotification(item)}
                          className={className}
                        >
                          {content}
                        </Link>
                      ) : (
                        <div
                          onClick={() => handleOpenNotification(item)}
                          className={className}
                        >
                          {content}
                        </div>
                      );
                    })()}
                    <Button
                      icon={RiCloseLine}
                      onClick={(e) => {
                        e.stopPropagation();
                        onClearNotification(item.id);
                      }}
                      variant="light"
                      color="gray"
                      size="xs"
                      tooltip="Clear notification"
                      className="absolute right-2 top-2 z-10 ml-auto flex-shrink-0 text-tremor-content hover:bg-tremor-background-muted dark:text-dark-tremor-content dark:hover:bg-dark-tremor-background-muted"
                    />
                  </Card>
                ))
              ) : (
                <Text className="text-center text-tremor-content-subtle dark:text-dark-tremor-content-subtle">
                  No new notifications.
                </Text>
              )}
            </div>
            <div className="border-t border-tremor-border px-6 py-3 dark:border-dark-tremor-border">
              <Link
                to="/notifications"
                onClick={() => setOpen(false)}
                className="block text-center text-sm font-medium text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
              >
                View all notifications
              </Link>
            </div>
          </div>
        </Transition.Child>
      </div>
    </Transition>
  );
}
